"use client";

import React from "react";
import { Leaf } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chapters = [{
  id: "the-foundation",
  title: "The Foundation",
  subtitle: "Discretion",
  quote: "True luxury lies in the unspoken details and absolute discretion of a professional guardian.",
  text: "GoldSteer was established with a singular vision: to redefine executive transport through a blend of old-world service and modern precision. Our chauffeurs are more than drivers; they are highly trained professionals who act as guardians of your time, safety, and absolute privacy.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/the_seed_founding.png",
  imageAlt: "A chauffeur in a crisp suit standing by a luxury vehicle in black and white",
  reverse: false
}, {
  id: "the-precision",
  title: "The Precision",
  subtitle: "Excellence",
  quote: "Precision is not just a standard; it is the core of every journey we facilitate for our clients.",
  text: "Excellence begins with preparation. From meticulously maintained vehicles to advanced route planning that accounts for every variable, we leave nothing to chance. Our commitment to precision ensures that you arrive not just on time, but in a state of complete composure and readiness.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/the_soil_sourcing.png",
  imageAlt: "Close up detail of a high-end luxury vehicle's polished exterior",
  reverse: true
}, {
  id: "the-standard",
  title: "The Standard",
  subtitle: "Luxury",
  quote: "A GoldSteer journey is an oasis of calm in a world that never stops moving at high speed.",
  text: "We provide a sanctuary on wheels. Every interior is a masterpiece of comfort, tailored for either focused productivity or total relaxation. Featuring the finest materials and climate-controlled environments, we offer the pinnacle of professional chauffeuring for the modern executive.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1598014/img/the_sip_quality.png",
  imageAlt: "Interior of a luxury sedan cabin showing premium leather and ambient lighting",
  reverse: false
}];
function ChapterBlock({
  chapter
}: {
  chapter: typeof chapters[0];
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <div data-section-id="153248" ref={ref} className={`flex flex-col gap-12 lg:gap-24 items-center ${chapter.reverse ? "md:flex-row-reverse" : "md:flex-row"} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} transition-all duration-1000 ease-out`}>
      {}
      <div className="w-full md:w-1/2">
        <div className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden rounded-sm shadow-2xl bg-muted">
          <img src={chapter.image} alt={chapter.imageAlt} className="object-cover w-full h-full transition-transform duration-1000 hover:scale-105 grayscale hover:grayscale-0" loading="lazy" />
          {}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
        </div>
      </div>

      {}
      <div className="w-full md:w-1/2 flex flex-col justify-center px-4 sm:px-0">
        <div className="flex items-center gap-4 mb-6">
          <span className="text-primary font-sans text-xs sm:text-sm font-bold tracking-[0.2em] uppercase">
            {chapter.title}
          </span>
          <Separator className="w-8 bg-primary/30" />
          <span className="text-muted-foreground font-sans text-xs sm:text-sm tracking-widest uppercase">
            {chapter.subtitle}
          </span>
        </div>
        
        <h3 className="font-default text-3xl md:text-4xl lg:text-5xl text-foreground leading-[1.15] italic mb-8">
          "{chapter.quote}"
        </h3>
        
        <p className="font-sans text-foreground/90 text-base md:text-lg leading-relaxed max-w-xl">
          {chapter.text}
        </p>
      </div>
    </div>;
}
function ChapterSeparator() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.5
  });
  return <div ref={ref} className={`flex items-center justify-center py-16 lg:py-24 ${isVisible ? "opacity-100 scale-100" : "opacity-0 scale-90"} transition-all duration-700 ease-out`}>
      <Separator className="w-16 md:w-32 bg-border/50" />
      <Leaf className="mx-6 text-primary/60 size-6 md:size-8" strokeWidth={1.5} />
      <Separator className="w-16 md:w-32 bg-border/50" />
    </div>;
}
export default function RootedInNature() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="about-us" className="bg-background text-foreground py-24 md:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-[120px]" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] rounded-full bg-secondary/10 blur-[100px]" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {}
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-20 md:mb-32 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} transition-all duration-1000 ease-out`}>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl text-foreground mb-6">
            The GoldSteer Standard
          </h2>
          <p className="font-sans text-lg text-muted-foreground leading-relaxed">
            Uncompromising values, a heritage of precision, and luxury standards that define every journey. Discover the GoldSteer experience.
          </p>
        </div>

        {}
        <div className="flex flex-col">
          {chapters.map((chapter, index) => <div key={chapter.id} data-index={index} className="w-full">
              <ChapterBlock chapter={chapter} />
              {index < chapters.length - 1 && <ChapterSeparator />}
            </div>)}
        </div>

        {}
        <div className="mt-24 md:mt-32 text-center flex flex-col items-center justify-center">
          <Separator className="w-24 bg-border mb-12" />
          <h4 className="font-default text-2xl md:text-3xl text-foreground mb-8 italic">
            Experience the standard for yourself.
          </h4>
          <Button asChild size="lg" className="rounded-none px-10 py-7 text-base tracking-widest uppercase bg-primary hover:bg-primary/90 text-primary-foreground transition-colors">
            <Link to="/ServicesPage">
              View Our Services
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}